<template>
	<div class="moments-stories-player">
		<div
			v-if="isModalMounted"
			class="moments-stories-player__modal"
			:class="{ '-open': isModalVisible }"
			@click.self="closeModal"
		>
			<div
				class="moments-stories-player__frame"
				@touchstart="onModalTouchStart"
				@touchend="onModalTouchEnd"
			>
				<div class="moments-stories-player__viewport">
					<div
						ref="currentStage"
						class="moments-stories-player__group-stage -current"
						:class="currentStageClass"
					>
						<video
							v-for="(slot, slotIndex) in videoSlots"
							:key="`active-slot-${slotIndex}`"
							ref="slotVideo"
							:class="['moments-stories-player__media', videoSlotClass(slotIndex)]"
							:src="slot.src || null"
							playsinline
							webkit-playsinline
							preload="auto"
							@loadedmetadata="handleVideoSlotLoadedMetadata(slotIndex)"
							@canplay="handleVideoSlotCanPlay(slotIndex)"
							@playing="handleVideoSlotPlaying(slotIndex)"
							@pause="handleVideoSlotPause(slotIndex)"
							@waiting="handleVideoSlotWaiting(slotIndex)"
							@stalled="handleVideoSlotWaiting(slotIndex)"
							@ended="handleVideoSlotEnded(slotIndex)"
						/>
					</div>

					<div
						ref="incomingStage"
						class="moments-stories-player__group-stage -incoming"
						:class="incomingStageClass"
					>
						<video
							ref="incomingVideo"
							class="moments-stories-player__media -active"
							playsinline
							webkit-playsinline
							preload="auto"
						/>
					</div>
					<div
						class="moments-stories-player__overlay-top-shadow"
						aria-hidden="true"
					/>

					<MomentsStoriesPlayerOverlay
						stage-type="current"
						:stage-class="currentStageClass"
						:story-items="activeStoryItems"
						:active-item-index="activeItemIndex"
						:active-item-progress="activeItemProgress"
						:group="currentGroup"
						:is-muted="isMuted"
						@close="closeModal"
						@toggle-mute="toggleMute"
					/>

					<MomentsStoriesPlayerOverlay
						stage-type="incoming"
						:stage-class="incomingStageClass"
						:story-items="incomingOverlayItems"
						:active-item-index="transitionToItemIndex"
						:active-item-progress="0"
						:group="incomingOverlayGroup"
						:is-muted="isMuted"
						@close="closeModal"
						@toggle-mute="toggleMute"
					/>

					<button
						type="button"
						class="moments-stories-player__nav -prev"
						aria-label="Previous"
						@mousedown.prevent="handleNavPressStart('previous', $event)"
						@mouseup.prevent="handleNavPressEnd($event)"
						@mouseleave="handleNavPressCancel"
						@touchstart.prevent="handleNavPressStart('previous', $event)"
						@touchend.prevent="handleNavPressEnd($event)"
						@touchcancel.prevent="handleNavPressCancel"
					/>
					<button
						type="button"
						class="moments-stories-player__nav -next"
						aria-label="Next"
						@mousedown.prevent="handleNavPressStart('next', $event)"
						@mouseup.prevent="handleNavPressEnd($event)"
						@mouseleave="handleNavPressCancel"
						@touchstart.prevent="handleNavPressStart('next', $event)"
						@touchend.prevent="handleNavPressEnd($event)"
						@touchcancel.prevent="handleNavPressCancel"
					/>

					<div
						v-if="showPlayerLoading"
						:class="[
							'moments-stories-player__loading',
							{ '-with-backdrop': showLoadingBackdrop },
						]"
						aria-hidden="true"
					>
						<div class="moments-stories-player__loading-spinner" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import MomentsStoriesPlayerOverlay from './MomentsStoriesPlayerOverlay';

export default {
	name:       'MomentsStoriesPlayer',
	components: {
		MomentsStoriesPlayerOverlay,
	},
	props: {
		items: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			isModalMounted:        false,
			isModalVisible:        false,
			isModalOpen:           false,
			activeGroupId:         null,
			activeItemIndex:       0,
			activeItemProgress:    0,
			currentVideoSlotIndex: 0,
			videoSlots:            [
				{ src: '', itemIndex: null },
				{ src: '', itemIndex: null },
			],
			isActiveVideoBuffering:            false,
			showActiveVideoBufferingIndicator: false,
			activeVideoBufferingTimer:         null,
			pendingGroupId:                    null,
			isResolvingPendingStoryOpen:       false,
			transitionToGroupId:               null,
			transitionToItemIndex:             0,
			transitionDirection:               'next',
			isGroupTransitioning:              false,
			isPreparingGroupTransition:        false,
			isCompletingGroupHandoff:          false,
			isIncomingGroupReady:              false,
			isIncomingStagePinned:             false,
			isCurrentStagePinned:              false,
			waitingIndicatorSuppressedUntilMs: 0,
			navTapThresholdMs:                 220,
			groupSwipeThresholdPx:             56,
			navPressState:                     {
				isActive:  false,
				direction: '',
				startedAt: 0,
				startX:    null,
				startY:    null,
				isTouch:   false,
			},
			isMuted:                       false,
			isBodyScrollLocked:            false,
			suppressPlayerLoadingOverlay:  false,
			modalCloseTimeoutId:           null,
			progressRafId:                 null,
			storyByGroupId:                {},
			storyBuildPromisesByGroupId:   {},
			remainingStoriesPrefetchQueue: [],
			isPrefetchingRemainingStories: false,
			remainingStoriesPrefetchTimer: null,
			nextStoryPrefetchTimer:        null,
			prewarmedVideoUrlsBySrc:       {},
			prewarmedVideoElementsBySrc:   {},
			prewarmedVideoPromisesBySrc:   {},
			incomingVideoAssignedSrc:      '',
			modalTouchStart:               null,
			bodyOverflowBackup:            '',
		};
	},
	computed: {
		activeStory() {
			return this.activeGroupId ? this.storyByGroupId[this.activeGroupId] || null : null;
		},
		activeStoryItems() {
			return this.activeStory?.items || [];
		},
		activeItem() {
			if (!this.activeStoryItems.length) {
				return null;
			}

			return this.activeStoryItems[this.activeItemIndex] || null;
		},
		currentGroup() {
			return this.findGroupById(this.activeGroupId);
		},
		incomingGroup() {
			return this.findGroupById(this.transitionToGroupId);
		},
		transitionToStory() {
			return this.transitionToGroupId ? this.storyByGroupId[this.transitionToGroupId] || null : null;
		},
		transitionToItem() {
			if (!this.transitionToStory?.items?.length) {
				return null;
			}

			return this.transitionToStory.items[this.transitionToItemIndex] || null;
		},
		currentStageClass() {
			if (!this.transitionToGroupId) {
				return '';
			}

			const classes = [];
			if (this.isGroupTransitioning) {
				classes.push(this.transitionDirection === 'previous' ? '-to-prev' : '-to-next');
				classes.push('-transitioning');
			}

			if (this.isCurrentStagePinned) {
				classes.push('-pinned');
			}

			return classes.join(' ');
		},
		incomingStageClass() {
			if (!this.transitionToGroupId) {
				return '-repositioning';
			}

			const directionClass = this.transitionDirection === 'previous' ? '-to-prev' : '-to-next';

			if (this.isGroupTransitioning) {
				return `${directionClass} -transitioning`;
			}

			if (this.isIncomingStagePinned) {
				return `${directionClass} -pinned`;
			}

			return `${directionClass} -repositioning`;
		},
		incomingOverlayItems() {
			return this.transitionToGroupId ? (this.transitionToStory?.items || []) : [];
		},
		incomingOverlayGroup() {
			return this.transitionToGroupId ? this.incomingGroup : null;
		},
		showPlayerLoading() {
			if (this.suppressPlayerLoadingOverlay) {
				return false;
			}

			return this.showActiveVideoBufferingIndicator;
		},
		showLoadingBackdrop() {
			return this.showActiveVideoBufferingIndicator;
		},
	},
	watch: {
		items: {
			handler() {
				this.resetForItemsChange();
			},
			deep: true,
		},
	},
	mounted() {
		this.resetForItemsChange();
		window.addEventListener('keydown', this.handleGlobalKeydown);
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.handleGlobalKeydown);
		this.clearModalState();
		this.clearPrefetchTimers();
		this.stopProgressTracking();
		this.releaseAllPrewarmedVideos();
	},
	methods: {
		resetForItemsChange() {
			this.clearModalState();
			this.clearPrefetchTimers();
			this.storyByGroupId = {};
			this.storyBuildPromisesByGroupId = {};
			this.prewarmedVideoUrlsBySrc = {};
			this.releaseAllPrewarmedVideos();
			this.pendingGroupId = null;
			this.isResolvingPendingStoryOpen = false;
			this.emitAvailability(this.items.length > 0);
			this.prefetchInitialPlayableStory();
		},
		clearModalState() {
			if (this.modalCloseTimeoutId) {
				window.clearTimeout(this.modalCloseTimeoutId);
				this.modalCloseTimeoutId = null;
			}

			if (this.isBodyScrollLocked) {
				document.body.style.overflow = this.bodyOverflowBackup;
				this.isBodyScrollLocked = false;
			}

			this.pauseAllVideoSlots();
			this.isModalMounted = false;
			this.isModalVisible = false;
			this.isModalOpen = false;
			this.activeGroupId = null;
			this.activeItemIndex = 0;
			this.activeItemProgress = 0;
			this.currentVideoSlotIndex = 0;
			this.videoSlots = [
				{ src: '', itemIndex: null },
				{ src: '', itemIndex: null },
			];
			this.setActiveVideoBuffering(false, { immediate: true });
			this.transitionToGroupId = null;
			this.transitionToItemIndex = 0;
			this.transitionDirection = 'next';
			this.isGroupTransitioning = false;
			this.isPreparingGroupTransition = false;
			this.isCompletingGroupHandoff = false;
			this.isIncomingGroupReady = false;
			this.isIncomingStagePinned = false;
			this.isCurrentStagePinned = false;
			this.waitingIndicatorSuppressedUntilMs = 0;
			this.navPressState = {
				isActive:  false,
				direction: '',
				startedAt: 0,
				startX:    null,
				startY:    null,
				isTouch:   false,
			};
			this.isMuted = false;
			this.suppressPlayerLoadingOverlay = false;
			this.modalTouchStart = null;
			this.stopProgressTracking();
			this.clearActiveVideoBufferingTimer();
			this.clearIncomingVideo();
		},
		clearPrefetchTimers() {
			if (this.remainingStoriesPrefetchTimer) {
				window.clearTimeout(this.remainingStoriesPrefetchTimer);
				this.remainingStoriesPrefetchTimer = null;
			}

			if (this.nextStoryPrefetchTimer) {
				window.clearTimeout(this.nextStoryPrefetchTimer);
				this.nextStoryPrefetchTimer = null;
			}

			this.remainingStoriesPrefetchQueue = [];
			this.isPrefetchingRemainingStories = false;
		},
		clearActiveVideoBufferingTimer() {
			if (this.activeVideoBufferingTimer) {
				window.clearTimeout(this.activeVideoBufferingTimer);
				this.activeVideoBufferingTimer = null;
			}
		},
		setActiveVideoBuffering(isBuffering, { immediate = false } = {}) {
			if (!isBuffering) {
				this.isActiveVideoBuffering = false;
				this.showActiveVideoBufferingIndicator = false;
				this.clearActiveVideoBufferingTimer();
				return;
			}

			this.isActiveVideoBuffering = true;
			this.clearActiveVideoBufferingTimer();

			if (immediate) {
				this.showActiveVideoBufferingIndicator = true;
				return;
			}

			this.activeVideoBufferingTimer = window.setTimeout(() => {
				this.activeVideoBufferingTimer = null;
				if (this.isActiveVideoBuffering) {
					this.showActiveVideoBufferingIndicator = true;
				}
			}, 220);
		},
		suppressWaitingIndicatorFor(durationMs = 0) {
			const normalizedDurationMs = Number(durationMs);
			if (!Number.isFinite(normalizedDurationMs) || normalizedDurationMs <= 0) {
				this.waitingIndicatorSuppressedUntilMs = 0;
				return;
			}

			this.waitingIndicatorSuppressedUntilMs = Date.now() + normalizedDurationMs;
		},
		releasePrewarmedVideo(videoSrc) {
			const normalizedVideoSrc = String(videoSrc || '');
			if (!normalizedVideoSrc) {
				return;
			}

			const video = this.prewarmedVideoElementsBySrc[normalizedVideoSrc];
			if (video) {
				video.removeAttribute('src');
				video.load();
			}

			const { [normalizedVideoSrc]: _prewarmedEntry, ...restPrewarmedUrls } = this.prewarmedVideoUrlsBySrc;
			this.prewarmedVideoUrlsBySrc = restPrewarmedUrls;

			const { [normalizedVideoSrc]: _prewarmedElement, ...restPrewarmedElements } = this.prewarmedVideoElementsBySrc;
			this.prewarmedVideoElementsBySrc = restPrewarmedElements;

			const { [normalizedVideoSrc]: _prewarmedPromise, ...restPrewarmedPromises } = this.prewarmedVideoPromisesBySrc;
			this.prewarmedVideoPromisesBySrc = restPrewarmedPromises;
		},
		releaseAllPrewarmedVideos() {
			Object.keys(this.prewarmedVideoElementsBySrc).forEach((videoSrc) => {
				this.releasePrewarmedVideo(videoSrc);
			});
		},
		async waitForVideoReady(video, timeoutMs = 700) {
			if (!video) {
				return false;
			}

			if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
				return true;
			}

			return new Promise((resolve) => {
				let isSettled = false;
				const settle = (isReady) => {
					if (isSettled) {
						return;
					}

					isSettled = true;
					window.clearTimeout(timeoutId);
					video.removeEventListener('loadeddata', onLoadedData);
					video.removeEventListener('canplay', onCanPlay);
					video.removeEventListener('error', onError);
					resolve(isReady);
				};
				const onLoadedData = () => settle(true);
				const onCanPlay = () => settle(true);
				const onError = () => settle(false);
				const timeoutId = window.setTimeout(() => settle(false), timeoutMs);

				video.addEventListener('loadeddata', onLoadedData, { once: true });
				video.addEventListener('canplay', onCanPlay, { once: true });
				video.addEventListener('error', onError, { once: true });
			});
		},
		async waitForVideoFirstFrame(video, timeoutMs = 220) {
			if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
				return false;
			}

			if (typeof video.requestVideoFrameCallback !== 'function') {
				await this.nextAnimationFrame();
				await this.nextAnimationFrame();
				return true;
			}

			return new Promise((resolve) => {
				let isSettled = false;
				const settle = (didRender) => {
					if (isSettled) {
						return;
					}

					isSettled = true;
					window.clearTimeout(timeoutId);
					resolve(didRender);
				};
				const timeoutId = window.setTimeout(() => settle(false), timeoutMs);

				video.requestVideoFrameCallback(() => settle(true));
			});
		},
		async waitForVideoRenderable(video, timeoutMs = 700) {
			const isReady = await this.waitForVideoReady(video, timeoutMs);
			if (!isReady) {
				return false;
			}

			return this.waitForVideoFirstFrame(video, Math.min(220, timeoutMs));
		},
		getVideoReadinessTimeouts() {
			if (this.isMobileViewport()) {
				return {
					slotPlaybackMs:    900,
					incomingPrepareMs: 1300,
					activeHandoffMs:   1000,
				};
			}

			return {
				slotPlaybackMs:    400,
				incomingPrepareMs: 450,
				activeHandoffMs:   700,
			};
		},
		async waitForVideoPlaying(video, timeoutMs = 900) {
			if (!video) {
				return false;
			}

			if (!video.paused && !video.ended && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
				return true;
			}

			return new Promise((resolve) => {
				let isSettled = false;
				const settle = (isPlaying) => {
					if (isSettled) {
						return;
					}

					isSettled = true;
					window.clearTimeout(timeoutId);
					video.removeEventListener('playing', onPlaying);
					video.removeEventListener('error', onError);
					resolve(isPlaying);
				};
				const onPlaying = () => settle(true);
				const onError = () => settle(false);
				const timeoutId = window.setTimeout(() => settle(false), timeoutMs);

				video.addEventListener('playing', onPlaying, { once: true });
				video.addEventListener('error', onError, { once: true });
			});
		},
		async waitForGroupTransitionEnd(timeoutMs = 380) {
			const stage = this.$refs.currentStage;
			if (!stage) {
				await this.wait(timeoutMs);
				return;
			}

			await new Promise((resolve) => {
				let isSettled = false;
				const settle = () => {
					if (isSettled) {
						return;
					}

					isSettled = true;
					window.clearTimeout(timeoutId);
					stage.removeEventListener('transitionend', onTransitionEnd);
					resolve();
				};
				const onTransitionEnd = (event) => {
					if (event.target !== stage || event.propertyName !== 'transform') {
						return;
					}

					settle();
				};
				const timeoutId = window.setTimeout(settle, timeoutMs);

				stage.addEventListener('transitionend', onTransitionEnd);
			});
		},
		findGroupById(groupId) {
			const normalizedGroupId = String(groupId || '');
			if (!normalizedGroupId) {
				return null;
			}

			return this.items.find(item => String(item.id) === normalizedGroupId) || null;
		},
		getGroupIds() {
			return this.items.map(item => String(item.id || '')).filter(Boolean);
		},
		getOrderedGroupIdsFrom(groupId, direction = 'next') {
			const normalizedGroupId = String(groupId || '');
			const groupIds = this.getGroupIds();
			if (!groupIds.length) {
				return [];
			}

			const currentIndex = groupIds.indexOf(normalizedGroupId);
			if (currentIndex < 0) {
				return groupIds;
			}

			if (direction === 'previous') {
				return [...groupIds.slice(0, currentIndex).reverse(), ...groupIds.slice(currentIndex + 1).reverse()];
			}

			return [...groupIds.slice(currentIndex + 1), ...groupIds.slice(0, currentIndex)];
		},
		getNavigationDirection(fromGroupId, toGroupId) {
			const groupIds = this.getGroupIds();
			const fromIndex = groupIds.indexOf(String(fromGroupId || ''));
			const toIndex = groupIds.indexOf(String(toGroupId || ''));
			if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
				return 'next';
			}

			return toIndex > fromIndex ? 'next' : 'previous';
		},
		async prefetchInitialPlayableStory() {
			const groupIds = this.getGroupIds();
			if (!groupIds.length) {
				this.emitAvailability(false);
				return;
			}

			for (const groupId of groupIds) {
				const story = await this.ensureStoryBuilt(groupId);
				if (story) {
					this.emitAvailability(true);
					return;
				}
			}

			this.emitAvailability(false);
		},
		async buildStory(group) {
			const groupMoments = await Promise.all((group.moments || []).map(async(momentItem) => {
				const src = typeof momentItem?.videoUrl === 'string' ? momentItem.videoUrl : '';
				const length = await this.resolveMomentLength(momentItem);

				return {
					id: momentItem.id,
					src,
					length,
				};
			}));

			const playableMoments = groupMoments.filter(moment => typeof moment.src === 'string' && moment.src.length > 0);
			if (!playableMoments.length) {
				return null;
			}

			return {
				id:    String(group.id),
				items: playableMoments.map(momentItem => ({
					id:     momentItem.id,
					type:   'video',
					length: momentItem.length,
					src:    momentItem.src,
				})),
			};
		},
		async ensureStoryBuilt(groupId) {
			const normalizedGroupId = String(groupId || '');
			if (!normalizedGroupId) {
				return null;
			}

			if (this.storyByGroupId[normalizedGroupId]) {
				return this.storyByGroupId[normalizedGroupId];
			}

			if (this.storyBuildPromisesByGroupId[normalizedGroupId]) {
				return this.storyBuildPromisesByGroupId[normalizedGroupId];
			}

			const group = this.items.find(item => String(item.id) === normalizedGroupId);
			if (!group) {
				return null;
			}

			const storyPromise = this.buildStory(group)
				.then((story) => {
					if (story) {
						this.storyByGroupId = {
							...this.storyByGroupId,
							[normalizedGroupId]: story,
						};
					}

					return story;
				})
				.finally(() => {
					const { [normalizedGroupId]: _buildPromise, ...restBuildPromises } = this.storyBuildPromisesByGroupId;
					this.storyBuildPromisesByGroupId = restBuildPromises;
				});

			this.storyBuildPromisesByGroupId = {
				...this.storyBuildPromisesByGroupId,
				[normalizedGroupId]: storyPromise,
			};

			return storyPromise;
		},
		openStory(groupId) {
			const normalizedGroupId = String(groupId || '');
			if (!normalizedGroupId) {
				return { waiting: false };
			}

			const canOpenImmediately = this.isModalOpen && this.activeGroupId === normalizedGroupId && !this.isGroupTransitioning;
			this.pendingGroupId = normalizedGroupId;
			this.resolvePendingStoryOpen();
			return { waiting: !canOpenImmediately };
		},
		async resolvePendingStoryOpen() {
			if (this.isResolvingPendingStoryOpen || !this.pendingGroupId) {
				return;
			}

			this.isResolvingPendingStoryOpen = true;
			try {
				while (this.pendingGroupId) {
					const targetGroupId = this.pendingGroupId;
					const story = await this.ensureStoryBuilt(targetGroupId);
					if (this.pendingGroupId !== targetGroupId) {
						continue;
					}

					if (!story) {
						this.pendingGroupId = null;
						this.$emit('story-open-failed', targetGroupId);
						continue;
					}

					if (!this.isModalOpen) {
						const didOpen = await this.openModalAt(targetGroupId, 0);
						if (!didOpen) {
							this.pendingGroupId = null;
							this.$emit('story-open-failed', targetGroupId);
							continue;
						}
					} else if (this.activeGroupId !== targetGroupId || this.isGroupTransitioning) {
						const direction = this.getNavigationDirection(this.activeGroupId, targetGroupId);
						await this.transitionToGroup(targetGroupId, direction, 0);
					}

					if (this.pendingGroupId !== targetGroupId) {
						continue;
					}

					this.pendingGroupId = null;
					this.$emit('story-opened', targetGroupId);
					this.queueNextStoryWarmup(targetGroupId);
					this.scheduleRemainingStoriesPrefetch(targetGroupId);
				}
			} finally {
				this.isResolvingPendingStoryOpen = false;
			}
		},
		async openModalAt(groupId, itemIndex = 0) {
			await this.prewarmStoryFirstVideo(groupId, true);

			this.activeGroupId = String(groupId);
			this.activeItemIndex = Math.max(0, itemIndex);
			this.activeItemProgress = 0;
			this.currentVideoSlotIndex = 0;
			this.isModalMounted = true;
			this.isModalVisible = false;
			this.isModalOpen = true;
			this.suppressPlayerLoadingOverlay = true;
			if (!this.isBodyScrollLocked) {
				this.bodyOverflowBackup = document.body.style.overflow;
				document.body.style.overflow = 'hidden';
				this.isBodyScrollLocked = true;
			}

			await this.$nextTick();
			this.syncVideoSlotsForActiveItem();
			await this.$nextTick();
			const didPlay = await this.playActiveVideo({
				startTime:    0,
				awaitPlaying: true,
			});
			if (!didPlay) {
				this.closeModal();
				return false;
			}

			this.isModalVisible = true;
			return didPlay;
		},
		closeModal() {
			if (!this.isModalMounted) {
				return;
			}

			if (this.modalCloseTimeoutId) {
				window.clearTimeout(this.modalCloseTimeoutId);
				this.modalCloseTimeoutId = null;
			}

			this.isModalOpen = false;
			this.pauseAllVideoSlots();
			this.pauseIncomingVideo();
			this.stopProgressTracking();
			this.clearNavPressState();
			this.setActiveVideoBuffering(false, { immediate: true });
			this.isGroupTransitioning = false;
			this.isIncomingGroupReady = false;
			this.isIncomingStagePinned = false;
			this.isCurrentStagePinned = false;
			this.transitionToGroupId = null;
			this.transitionToItemIndex = 0;
			this.suppressPlayerLoadingOverlay = false;
			this.isModalVisible = false;
			this.modalCloseTimeoutId = window.setTimeout(() => {
				this.modalCloseTimeoutId = null;
				if (!this.isModalVisible) {
					this.clearModalState();
				}
			}, 220);
		},
		queueNextStoryWarmup(groupId) {
			if (this.nextStoryPrefetchTimer) {
				window.clearTimeout(this.nextStoryPrefetchTimer);
			}

			this.nextStoryPrefetchTimer = window.setTimeout(async() => {
				this.nextStoryPrefetchTimer = null;
				const nextGroupId = await this.ensureNextPlayableStory(groupId);
				await this.prewarmStoryFirstVideo(nextGroupId);
				this.warmIncomingVideo(nextGroupId, 0);
			}, 300);
		},
		scheduleRemainingStoriesPrefetch(groupId) {
			const orderedGroupIds = this.getOrderedGroupIdsFrom(groupId, 'next')
				.filter(remainingGroupId => !this.storyByGroupId[remainingGroupId]);

			if (!orderedGroupIds.length) {
				return;
			}

			const existingQueue = new Set(this.remainingStoriesPrefetchQueue);
			const mergedQueue = [...this.remainingStoriesPrefetchQueue];
			orderedGroupIds.forEach((remainingGroupId) => {
				if (existingQueue.has(remainingGroupId)) {
					return;
				}

				existingQueue.add(remainingGroupId);
				mergedQueue.push(remainingGroupId);
			});

			this.remainingStoriesPrefetchQueue = mergedQueue;
			if (this.remainingStoriesPrefetchTimer || this.isPrefetchingRemainingStories) {
				return;
			}

			this.remainingStoriesPrefetchTimer = window.setTimeout(() => {
				this.remainingStoriesPrefetchTimer = null;
				this.prefetchRemainingStoriesInBackground();
			}, 1200);
		},
		async prefetchRemainingStoriesInBackground() {
			if (this.isPrefetchingRemainingStories) {
				return;
			}

			this.isPrefetchingRemainingStories = true;
			try {
				while (this.remainingStoriesPrefetchQueue.length) {
					const groupId = this.remainingStoriesPrefetchQueue.shift();
					await this.ensureStoryBuilt(groupId);
					await this.wait(120);
				}
			} finally {
				this.isPrefetchingRemainingStories = false;
			}
		},
		async ensureNextPlayableStory(groupId, { allowWrap = true } = {}) {
			let orderedGroupIds = this.getOrderedGroupIdsFrom(groupId, 'next');
			if (!allowWrap) {
				const normalizedGroupId = String(groupId || '');
				const groupIds = this.getGroupIds();
				const currentIndex = groupIds.indexOf(normalizedGroupId);
				orderedGroupIds = currentIndex < 0 ? groupIds : groupIds.slice(currentIndex + 1);
			}

			for (const nextGroupId of orderedGroupIds) {
				const story = await this.ensureStoryBuilt(nextGroupId);
				if (story) {
					return nextGroupId;
				}
			}

			return '';
		},
		async ensurePreviousPlayableStory(groupId) {
			const orderedGroupIds = this.getOrderedGroupIdsFrom(groupId, 'previous');
			for (const previousGroupId of orderedGroupIds) {
				const story = await this.ensureStoryBuilt(previousGroupId);
				if (story) {
					return previousGroupId;
				}
			}

			return '';
		},
		async transitionToGroup(groupId, direction = 'next', startItemIndex = 0) {
			const normalizedGroupId = String(groupId || '');
			if (!normalizedGroupId || this.activeGroupId === normalizedGroupId) {
				this.activeItemIndex = Math.max(0, startItemIndex);
				this.activeItemProgress = 0;
				this.currentVideoSlotIndex = 0;
				await this.$nextTick();
				this.syncVideoSlotsForActiveItem();
				await this.$nextTick();
				this.playActiveVideo();
				return;
			}

			const story = await this.ensureStoryBuilt(normalizedGroupId);
			if (!story) {
				return;
			}

			this.isPreparingGroupTransition = true;
			try {
				const readinessTimeouts = this.getVideoReadinessTimeouts();
				this.setActiveVideoBuffering(false, { immediate: true });
				this.suppressPlayerLoadingOverlay = true;
				this.suppressWaitingIndicatorFor(1200);
				this.transitionToGroupId = normalizedGroupId;
				this.transitionToItemIndex = Math.max(0, Math.min(startItemIndex, story.items.length - 1));
				this.transitionDirection = direction === 'previous' ? 'previous' : 'next';
				this.isIncomingGroupReady = false;
				this.isGroupTransitioning = false;
				this.isIncomingStagePinned = false;
				this.isCurrentStagePinned = false;
				this.isCompletingGroupHandoff = false;

				const targetSrc = story.items[this.transitionToItemIndex]?.src || story.items[0]?.src || '';
				const incomingVideo = this.$refs.incomingVideo;

				if (incomingVideo && this.incomingVideoAssignedSrc !== targetSrc && targetSrc) {
					incomingVideo.src = targetSrc;
					incomingVideo.muted = true;
					incomingVideo.preload = 'auto';
					incomingVideo.load();
					this.incomingVideoAssignedSrc = targetSrc;
				}

				await this.$nextTick();
				await this.prewarmStoryFirstVideo(normalizedGroupId, true, this.transitionToItemIndex);

				let isIncomingReady = incomingVideo?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA;
				if (!isIncomingReady && incomingVideo) {
					isIncomingReady = await this.waitForVideoRenderable(incomingVideo, readinessTimeouts.incomingPrepareMs);
				}

				if (!isIncomingReady) {
					this.setActiveVideoBuffering(true, { immediate: true });
					this.suppressPlayerLoadingOverlay = false;
				}

				if (incomingVideo && this.isLikelyIosWebkit()) {
					incomingVideo.muted = true;
					incomingVideo.currentTime = this.getInitialPlaybackTime(incomingVideo, 0);
					await this.safePlay(incomingVideo);
					await this.waitForVideoFirstFrame(incomingVideo, 260);
					try {
						incomingVideo.pause();
					} catch {
						// Ignore pause errors.
					}
				}

				this.isIncomingGroupReady = isIncomingReady;
				await this.$nextTick();
				await this.nextAnimationFrame();
				this.isGroupTransitioning = true;
				await this.waitForGroupTransitionEnd();

				const outgoingVideo = this.getCurrentVideoElement();
				if (outgoingVideo) {
					outgoingVideo.muted = true;
				}
				this.stopProgressTracking();

				this.pauseCurrentVideo();

				this.activeGroupId = normalizedGroupId;
				this.activeItemIndex = this.transitionToItemIndex;
				this.activeItemProgress = 0;
				this.isIncomingStagePinned = true;
				this.isCurrentStagePinned = true;
				this.isGroupTransitioning = false;
				this.isCompletingGroupHandoff = true;

				await this.$nextTick();
				this.currentVideoSlotIndex = 0;
				this.syncVideoSlotsForActiveItem();
				await this.$nextTick();

				const activeVideo = this.getCurrentVideoElement();
				let didRenderActiveFrame = false;

				if (activeVideo) {
					this.pauseAllVideoSlots();
					await this.waitForVideoReady(activeVideo, readinessTimeouts.activeHandoffMs);
					if (this.isLikelyIosWebkit()) {
						activeVideo.currentTime = this.getInitialPlaybackTime(activeVideo, 0);
						activeVideo.muted = true;
						const didPlay = await this.safePlay(activeVideo);
						didRenderActiveFrame = didPlay && await this.waitForVideoPlaybackAdvance(activeVideo, {
							timeoutMs:         Math.max(420, readinessTimeouts.activeHandoffMs),
							minAdvanceSeconds: 0.05,
						});
						if (!didRenderActiveFrame && didPlay) {
							didRenderActiveFrame = await this.waitForVideoFirstFrame(activeVideo, 320);
						}
					}
				}

				this.transitionToGroupId = null;
				this.transitionToItemIndex = 0;
				this.isIncomingGroupReady = false;
				this.isIncomingStagePinned = false;
				this.isCurrentStagePinned = false;
				this.isCompletingGroupHandoff = false;
				this.suppressPlayerLoadingOverlay = false;
				this.suppressWaitingIndicatorFor(didRenderActiveFrame ? 260 : 420);
				this.setActiveVideoBuffering(false, { immediate: true });

				let didResumeActive = await this.playWithPreferredAudio(activeVideo, { awaitPlaying: true });
				if (!didResumeActive) {
					didResumeActive = await this.playActiveVideo({
						showBufferingIndicator: false,
						awaitPlaying:           true,
					});
				}

				if (incomingVideo) {
					try {
						incomingVideo.pause();
					} catch {
						// Ignore pause errors.
					}
					incomingVideo.muted = true;
				}

				if (didResumeActive) {
					this.startProgressTracking();
				}
				this.prewarmNextItemInActiveGroup();
			} finally {
				this.isPreparingGroupTransition = false;
				this.isCompletingGroupHandoff = false;
			}
		},
		videoSlotClass(slotIndex) {
			return slotIndex === this.currentVideoSlotIndex ? '-active' : '-idle';
		},
		getStandbyVideoSlotIndex(slotIndex = this.currentVideoSlotIndex) {
			return this.getNextVideoSlotIndex(slotIndex);
		},
		getNextVideoSlotIndex(slotIndex = this.currentVideoSlotIndex) {
			return (slotIndex + 1) % this.videoSlots.length;
		},
		getVideoSlotElements() {
			if (!Array.isArray(this.$refs.slotVideo)) {
				return this.$refs.slotVideo ? [this.$refs.slotVideo] : [];
			}

			return this.$refs.slotVideo;
		},
		getVideoSlotElement(slotIndex) {
			return this.getVideoSlotElements()[slotIndex] || null;
		},
		getCurrentVideoElement() {
			return this.getVideoSlotElement(this.currentVideoSlotIndex);
		},
		getCurrentOrIncomingVideoElement() {
			return this.getCurrentVideoElement() || this.$refs.incomingVideo || null;
		},
		getPlaybackVideoElements() {
			const slotVideos = this.getVideoSlotElements();
			if (this.isIncomingStagePinned || this.isCompletingGroupHandoff) {
				const incomingVideo = this.$refs.incomingVideo;
				return incomingVideo ? [...slotVideos, incomingVideo] : slotVideos;
			}
			return slotVideos;
		},
		applyMutedStateToPlaybackElements() {
			this.getPlaybackVideoElements().forEach((video) => {
				video.muted = this.isMuted;
			});
		},
		async safePlay(video) {
			if (!video) {
				return false;
			}

			try {
				await video.play();
				return true;
			} catch {
				return false;
			}
		},
		async playWithPreferredAudio(video, { awaitPlaying = false } = {}) {
			if (!video) {
				return false;
			}

			video.muted = this.isMuted;
			let didPlay = await this.safePlay(video);
			if (!didPlay && !this.isMuted) {
				// Keep user mute preference and only use muted playback as a per-video fallback.
				video.muted = true;
				didPlay = await this.safePlay(video);
				if (didPlay) {
					video.muted = false;
				}
			}

			if (!didPlay) {
				return false;
			}

			if (!awaitPlaying) {
				return true;
			}

			return this.waitForVideoPlaying(video, 900);
		},
		isLikelyIosWebkit() {
			if (typeof window === 'undefined') {
				return false;
			}

			const userAgent = String(window.navigator?.userAgent || '');
			const isAppleMobile = /iP(ad|hone|od)/.test(userAgent);
			const isWebkit = /AppleWebKit/i.test(userAgent);
			const isAltBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);
			return isAppleMobile && isWebkit && !isAltBrowser;
		},
		async waitForVideoPlaybackAdvance(video, { timeoutMs = 420, minAdvanceSeconds = 0.05 } = {}) {
			if (!video) {
				return false;
			}

			const baseline = Number(video.currentTime) || 0;
			const target = baseline + Math.max(0.02, Number(minAdvanceSeconds) || 0.05);
			const startedAt = Date.now();

			return new Promise((resolve) => {
				const check = () => {
					const currentTime = Number(video.currentTime) || 0;
					if (!video.paused && currentTime >= target) {
						resolve(true);
						return;
					}

					if (Date.now() - startedAt >= timeoutMs) {
						resolve(false);
						return;
					}

					window.requestAnimationFrame(check);
				};

				check();
			});
		},
		getInitialPlaybackTime(video, startTime = 0) {
			const numericStartTime = Number(startTime);
			if (!Number.isFinite(numericStartTime) || numericStartTime < 0) {
				return 0;
			}

			if (numericStartTime > 0) {
				return numericStartTime;
			}

			const twoFramesOffsetSeconds = 0.08;
			const duration = Number(video?.duration);
			if (!Number.isFinite(duration) || duration <= 0) {
				return twoFramesOffsetSeconds;
			}

			return Math.max(0, Math.min(twoFramesOffsetSeconds, duration - 0.01));
		},
		async toggleMute() {
			this.isMuted = !this.isMuted;
			this.applyMutedStateToPlaybackElements();

			if (this.isMuted) {
				return;
			}

			const video = this.getCurrentOrIncomingVideoElement();
			if (!video) {
				return;
			}

			const didPlayWithSound = await this.playWithPreferredAudio(video, { awaitPlaying: false });
			if (!didPlayWithSound) {
				this.isMuted = true;
				this.applyMutedStateToPlaybackElements();
			}
		},
		setVideoSlotSource(slotIndex, itemIndex, item) {
			const src = item?.src || '';
			const currentSlot = this.videoSlots[slotIndex];
			if (!currentSlot) {
				return;
			}

			const normalizedItemIndex = Number.isInteger(itemIndex) ? itemIndex : null;
			if (currentSlot.src === src && currentSlot.itemIndex === normalizedItemIndex) {
				return;
			}

			this.$set(this.videoSlots, slotIndex, {
				src,
				itemIndex: normalizedItemIndex,
			});
		},
		syncVideoSlotsForActiveItem() {
			const storyItems = this.activeStoryItems;
			if (!storyItems.length) {
				this.videoSlots.forEach((_slot, slotIndex) => {
					this.setVideoSlotSource(slotIndex, null, null);
				});
				return;
			}

			const currentIndex = this.activeItemIndex;
			const nextIndex = currentIndex + 1;
			const currentSlotIndex = this.currentVideoSlotIndex;
			const standbySlotIndex = this.getStandbyVideoSlotIndex(currentSlotIndex);

			this.setVideoSlotSource(currentSlotIndex, currentIndex, storyItems[currentIndex] || null);
			this.setVideoSlotSource(standbySlotIndex, nextIndex, storyItems[nextIndex] || null);
			this.prewarmNextItemInActiveGroup();
		},
		async switchToItemWithDualVideo(targetItemIndex) {
			const normalizedTargetIndex = Number(targetItemIndex);
			if (!Number.isInteger(normalizedTargetIndex) || normalizedTargetIndex < 0 || normalizedTargetIndex >= this.activeStoryItems.length) {
				return false;
			}

			const standbySlotIndex = this.getStandbyVideoSlotIndex();
			const standbyItem = this.activeStoryItems[normalizedTargetIndex] || null;
			this.setVideoSlotSource(standbySlotIndex, normalizedTargetIndex, standbyItem);
			await this.$nextTick();

			const standbyVideo = this.getVideoSlotElement(standbySlotIndex);
			if (!standbyVideo) {
				return false;
			}

			const readinessTimeouts = this.getVideoReadinessTimeouts();
			const isReady = await this.waitForVideoReady(standbyVideo, readinessTimeouts.slotPlaybackMs);
			if (isReady && this.isLikelyIosWebkit()) {
				standbyVideo.currentTime = this.getInitialPlaybackTime(standbyVideo, 0);
				standbyVideo.muted = true;
				await this.safePlay(standbyVideo);
				const didAdvanceWhileStandby = await this.waitForVideoPlaybackAdvance(standbyVideo, {
					timeoutMs:         300,
					minAdvanceSeconds: 0.04,
				});
				if (!didAdvanceWhileStandby) {
					await this.waitForVideoFirstFrame(standbyVideo, 280);
				}
			}

			const outgoingVideo = this.getCurrentVideoElement();
			this.currentVideoSlotIndex = standbySlotIndex;
			this.activeItemIndex = normalizedTargetIndex;
			this.activeItemProgress = 0;
			this.syncVideoSlotsForActiveItem();
			await this.$nextTick();

			const activeVideo = this.getCurrentVideoElement();
			const didPlay = await this.playWithPreferredAudio(activeVideo, { awaitPlaying: false });
			if (!didPlay) {
				await this.playActiveVideo({ showBufferingIndicator: false });
			} else {
				this.suppressPlayerLoadingOverlay = false;
				this.setActiveVideoBuffering(false, { immediate: true });
				this.startProgressTracking();
			}

			if (outgoingVideo && outgoingVideo !== activeVideo) {
				try {
					outgoingVideo.pause();
				} catch {
					// Ignore pause errors.
				}
			}

			return true;
		},
		markVideoSrcPrewarmed(videoSrc) {
			const normalizedVideoSrc = String(videoSrc || '');
			if (!normalizedVideoSrc || this.prewarmedVideoUrlsBySrc[normalizedVideoSrc]) {
				return;
			}

			this.prewarmedVideoUrlsBySrc = {
				...this.prewarmedVideoUrlsBySrc,
				[normalizedVideoSrc]: true,
			};
		},
		async playActiveVideo({ startTime = 0, awaitPlaying = false, showBufferingIndicator = true } = {}) {
			await this.$nextTick();
			const video = this.getCurrentVideoElement();
			if (!video) {
				return false;
			}
			const readinessTimeouts = this.getVideoReadinessTimeouts();

			this.pauseAllVideoSlots();
			video.currentTime = this.getInitialPlaybackTime(video, startTime);

			const shouldShowInitialBuffering = showBufferingIndicator && video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA;
			if (shouldShowInitialBuffering) {
				this.setActiveVideoBuffering(true);
				await this.waitForVideoReady(video, readinessTimeouts.slotPlaybackMs);
			} else {
				this.setActiveVideoBuffering(false, { immediate: true });
			}
			this.prewarmNextItemInActiveGroup();
			const didPlay = await this.playWithPreferredAudio(video, { awaitPlaying });
			if (!didPlay) {
				this.setActiveVideoBuffering(false, { immediate: true });
				return false;
			}

			return true;
		},
		pauseCurrentVideo() {
			const video = this.getCurrentVideoElement();
			if (video) {
				try {
					video.pause();
				} catch {
					// Ignore pause errors.
				}
			}

			this.stopProgressTracking();
		},
		pauseAllVideoSlots() {
			this.getVideoSlotElements().forEach((video) => {
				try {
					video.pause();
				} catch {
					// Ignore pause errors.
				}
			});
		},
		handleVideoSlotLoadedMetadata(slotIndex) {
			const video = this.getVideoSlotElement(slotIndex);
			if (!video) {
				return;
			}

			this.markVideoSrcPrewarmed(video.currentSrc || video.src);
			if (slotIndex !== this.currentVideoSlotIndex) {
				return;
			}

			const duration = Number(video.duration);
			if (!Number.isFinite(duration) || duration <= 0) {
				return;
			}

			this.activeItemProgress = Math.max(0, Math.min(1, Number(video.currentTime) / duration));
			this.prewarmNextItemInActiveGroup();
		},
		handleVideoSlotCanPlay(slotIndex) {
			const video = this.getVideoSlotElement(slotIndex);
			if (!video) {
				return;
			}

			this.markVideoSrcPrewarmed(video.currentSrc || video.src);
		},
		handleVideoSlotPlaying(slotIndex) {
			if (slotIndex !== this.currentVideoSlotIndex) {
				return;
			}

			this.suppressPlayerLoadingOverlay = false;
			this.setActiveVideoBuffering(false, { immediate: true });
			this.startProgressTracking();
			this.prewarmNextItemInActiveGroup();
		},
		handleVideoSlotPause(slotIndex) {
			if (slotIndex !== this.currentVideoSlotIndex) {
				return;
			}

			this.stopProgressTracking();
		},
		handleVideoSlotWaiting(slotIndex) {
			if (slotIndex !== this.currentVideoSlotIndex) {
				return;
			}

			if (this.isPreparingGroupTransition || this.transitionToGroupId || this.isCompletingGroupHandoff) {
				return;
			}

			if (this.waitingIndicatorSuppressedUntilMs && Date.now() < this.waitingIndicatorSuppressedUntilMs) {
				return;
			}

			const video = this.getCurrentVideoElement();
			if (video) {
				const duration = Number(video.duration);
				const current = Number(video.currentTime);
				if (Number.isFinite(duration) && duration > 0 && Number.isFinite(current)) {
					const remaining = Math.max(0, duration - current);
					const isNearEnd = remaining <= 0.25 || current / duration >= 0.96;
					if (isNearEnd) {
						return;
					}
				}
			}

			this.setActiveVideoBuffering(true);
			this.stopProgressTracking();
		},
		async handleVideoSlotEnded(slotIndex) {
			if (slotIndex !== this.currentVideoSlotIndex) {
				return;
			}

			this.setActiveVideoBuffering(false, { immediate: true });
			this.stopProgressTracking();
			await this.handleNextAction();
		},
		startProgressTracking() {
			this.stopProgressTracking();
			const video = this.getCurrentVideoElement();
			if (!video) {
				return;
			}

			const tick = () => {
				const currentVideo = this.getCurrentVideoElement();
				if (!this.isModalOpen || !currentVideo) {
					this.progressRafId = null;
					return;
				}

				const duration = Number(currentVideo.duration) || Number(this.activeItem?.length) || 1;
				const current = Number(currentVideo.currentTime) || 0;
				this.activeItemProgress = Math.max(0, Math.min(1, current / duration));

				if (!currentVideo.paused && !currentVideo.ended) {
					this.progressRafId = window.requestAnimationFrame(tick);
				} else {
					this.progressRafId = null;
				}
			};

			this.progressRafId = window.requestAnimationFrame(tick);
		},
		stopProgressTracking() {
			if (this.progressRafId) {
				window.cancelAnimationFrame(this.progressRafId);
				this.progressRafId = null;
			}
		},
		getPointerPosition(event) {
			const changedTouch = event?.changedTouches?.[0];
			if (changedTouch) {
				return { x: changedTouch.clientX, y: changedTouch.clientY };
			}

			const touch = event?.touches?.[0];
			if (touch) {
				return { x: touch.clientX, y: touch.clientY };
			}

			if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) {
				return { x: event.clientX, y: event.clientY };
			}

			return null;
		},
		getNavSwipeDeltaX(event, pressState = this.navPressState) {
			if (!pressState.isTouch || !Number.isFinite(pressState.startX) || !Number.isFinite(pressState.startY)) {
				return null;
			}

			const pointerPosition = this.getPointerPosition(event);
			if (!pointerPosition) {
				return null;
			}

			const deltaX = pointerPosition.x - pressState.startX;
			const deltaY = pointerPosition.y - pressState.startY;
			const isHorizontalSwipe = Math.abs(deltaX) >= this.groupSwipeThresholdPx && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;
			if (!isHorizontalSwipe) {
				return null;
			}

			return deltaX;
		},
		clearNavPressState() {
			this.navPressState = {
				isActive:  false,
				direction: '',
				startedAt: 0,
				startX:    null,
				startY:    null,
				isTouch:   false,
			};
		},
		handleNavPressStart(direction, event) {
			if (!this.isModalOpen || this.isGroupTransitioning) {
				return;
			}

			const pointerPosition = this.getPointerPosition(event);
			const isTouchEvent = Boolean(event?.changedTouches || event?.touches);
			this.navPressState = {
				isActive:  true,
				direction,
				startedAt: Date.now(),
				startX:    pointerPosition?.x ?? null,
				startY:    pointerPosition?.y ?? null,
				isTouch:   isTouchEvent,
			};

			this.pauseCurrentVideo();
		},
		async handleNavPressEnd(event) {
			if (!this.navPressState.isActive) {
				return;
			}

			const deltaX = this.getNavSwipeDeltaX(event, this.navPressState);
			const { direction, startedAt } = this.navPressState;
			this.clearNavPressState();

			if (Number.isFinite(deltaX)) {
				await this.handleGroupSwipeNavigation(deltaX > 0 ? 'previous' : 'next');
				return;
			}

			const pressDurationMs = Date.now() - startedAt;
			if (pressDurationMs <= this.navTapThresholdMs) {
				if (direction === 'previous') {
					await this.handlePreviousAction();
					return;
				}

				await this.handleNextAction();
				return;
			}

			await this.resumeCurrentVideoAfterHold();
		},
		async handleGroupSwipeNavigation(direction) {
			if (!this.isModalOpen || this.isGroupTransitioning) {
				return;
			}

			if (direction === 'next') {
				const nextGroupId = await this.ensureNextPlayableStory(this.activeGroupId, { allowWrap: false });
				if (!nextGroupId) {
					return;
				}

				await this.transitionToGroup(nextGroupId, 'next', 0);
				this.queueNextStoryWarmup(nextGroupId);
				this.scheduleRemainingStoriesPrefetch(nextGroupId);
				return;
			}

			const previousGroupId = await this.ensurePreviousPlayableStory(this.activeGroupId);
			if (!previousGroupId) {
				return;
			}

			await this.transitionToGroup(previousGroupId, 'previous', 0);
			this.queueNextStoryWarmup(previousGroupId);
		},
		async handleNavPressCancel() {
			if (!this.navPressState.isActive) {
				return;
			}

			this.clearNavPressState();
			await this.resumeCurrentVideoAfterHold();
		},
		async resumeCurrentVideoAfterHold() {
			if (!this.isModalOpen || this.isGroupTransitioning) {
				return;
			}

			const video = this.getCurrentVideoElement();
			if (!video) {
				return;
			}

			this.prewarmNextItemInActiveGroup();
			await this.playWithPreferredAudio(video, { awaitPlaying: false });
		},
		async handleNextAction() {
			if (!this.isModalOpen || this.isGroupTransitioning) {
				return;
			}

			this.setActiveVideoBuffering(false, { immediate: true });

			if (this.activeItemIndex < this.activeStoryItems.length - 1) {
				await this.switchToItemWithDualVideo(this.activeItemIndex + 1);
				return;
			}

			if (this.activeGroupId) {
				this.$emit('group-complete', this.activeGroupId);
			}

			const nextGroupId = await this.ensureNextPlayableStory(this.activeGroupId, { allowWrap: false });
			if (!nextGroupId) {
				this.closeModal();
				return;
			}

			await this.transitionToGroup(nextGroupId, 'next', 0);
			this.queueNextStoryWarmup(nextGroupId);
			this.scheduleRemainingStoriesPrefetch(nextGroupId);
		},
		async handlePreviousAction() {
			if (!this.isModalOpen || this.isGroupTransitioning) {
				return;
			}

			this.setActiveVideoBuffering(false, { immediate: true });

			const activeVideo = this.getCurrentVideoElement();
			if (activeVideo && Number(activeVideo.currentTime) > 2) {
				activeVideo.currentTime = this.getInitialPlaybackTime(activeVideo, 0);
				this.activeItemProgress = 0;
				return;
			}

			if (this.activeItemIndex > 0) {
				await this.switchToItemWithDualVideo(this.activeItemIndex - 1);
				return;
			}

			const previousGroupId = await this.ensurePreviousPlayableStory(this.activeGroupId);
			if (!previousGroupId) {
				return;
			}

			const previousStory = this.storyByGroupId[previousGroupId];
			const lastItemIndex = Math.max(0, (previousStory?.items?.length || 1) - 1);
			await this.transitionToGroup(previousGroupId, 'previous', lastItemIndex);
			this.queueNextStoryWarmup(previousGroupId);
		},
		handleGlobalKeydown(event) {
			if (!this.isModalOpen) {
				return;
			}

			if (event.key === 'Escape') {
				event.preventDefault();
				this.closeModal();
				return;
			}

			if (event.key === 'ArrowRight') {
				event.preventDefault();
				this.handleNextAction();
				return;
			}

			if (event.key === 'ArrowLeft') {
				event.preventDefault();
				this.handlePreviousAction();
			}
		},
		onModalTouchStart(event) {
			if (!this.isMobileViewport()) {
				return;
			}

			const touch = event.changedTouches?.[0];
			if (!touch) {
				return;
			}

			this.modalTouchStart = {
				x: touch.clientX,
				y: touch.clientY,
			};
		},
		onModalTouchEnd(event) {
			if (!this.isMobileViewport() || !this.modalTouchStart) {
				return;
			}

			const touch = event.changedTouches?.[0];
			if (!touch) {
				this.modalTouchStart = null;
				return;
			}

			const deltaX = touch.clientX - this.modalTouchStart.x;
			const deltaY = touch.clientY - this.modalTouchStart.y;
			this.modalTouchStart = null;

			const isSwipeDown = deltaY > 80 && Math.abs(deltaY) > Math.abs(deltaX) * 1.2;
			if (isSwipeDown) {
				this.closeModal();
			}
		},
		isMobileViewport() {
			if (typeof window === 'undefined') {
				return false;
			}

			return window.matchMedia('(max-width: 1024px)').matches;
		},
		async prewarmStoryFirstVideo(groupId, shouldAwaitReady = false, itemIndex = 0) {
			const normalizedGroupId = String(groupId || '');
			if (!normalizedGroupId) {
				return;
			}

			const story = this.storyByGroupId[normalizedGroupId];
			const normalizedItemIndex = Number.isInteger(itemIndex) ? itemIndex : 0;
			const targetItemSrc = story?.items?.[normalizedItemIndex]?.src || story?.items?.[0]?.src || '';
			await this.prewarmVideoSrc(targetItemSrc, { shouldAwaitReady });
		},
		async prewarmVideoSrc(videoSrc, { shouldAwaitReady = false } = {}) {
			const normalizedVideoSrc = String(videoSrc || '');
			if (!normalizedVideoSrc) {
				return;
			}

			if (this.prewarmedVideoUrlsBySrc[normalizedVideoSrc]) {
				return true;
			}

			if (!this.prewarmedVideoElementsBySrc[normalizedVideoSrc]) {
				const existingSrcs = Object.keys(this.prewarmedVideoElementsBySrc);
				while (existingSrcs.length >= 2) {
					this.releasePrewarmedVideo(existingSrcs.shift());
				}

				const video = document.createElement('video');
				video.preload = 'auto';
				video.muted = true;
				video.playsInline = true;
				video.src = normalizedVideoSrc;
				video.load();

				this.prewarmedVideoElementsBySrc = {
					...this.prewarmedVideoElementsBySrc,
					[normalizedVideoSrc]: video,
				};
			}

			if (!this.prewarmedVideoPromisesBySrc[normalizedVideoSrc]) {
				const video = this.prewarmedVideoElementsBySrc[normalizedVideoSrc];
				const preloadPromise = this.waitForVideoReady(video, 900).then((isReady) => {
					if (isReady) {
						this.prewarmedVideoUrlsBySrc = {
							...this.prewarmedVideoUrlsBySrc,
							[normalizedVideoSrc]: true,
						};
						return true;
					}

					this.releasePrewarmedVideo(normalizedVideoSrc);
					return false;
				});

				this.prewarmedVideoPromisesBySrc = {
					...this.prewarmedVideoPromisesBySrc,
					[normalizedVideoSrc]: preloadPromise,
				};
			}

			if (!shouldAwaitReady) {
				return false;
			}

			return this.prewarmedVideoPromisesBySrc[normalizedVideoSrc];
		},
		prewarmNextItemInActiveGroup() {
			const nextItem = this.activeStoryItems[this.activeItemIndex + 2];
			if (!nextItem?.src) {
				return;
			}

			this.prewarmVideoSrc(nextItem.src);
		},
		warmIncomingVideo(groupId, itemIndex = 0) {
			const normalizedGroupId = String(groupId || '');
			if (!normalizedGroupId) {
				return;
			}

			const story = this.storyByGroupId[normalizedGroupId];
			const idx = Number.isInteger(itemIndex) ? itemIndex : 0;
			const src = story?.items?.[idx]?.src || story?.items?.[0]?.src || '';
			if (!src || this.incomingVideoAssignedSrc === src) {
				return;
			}

			const video = this.$refs.incomingVideo;
			if (!video) {
				return;
			}

			video.src = src;
			video.muted = true;
			video.preload = 'auto';
			video.load();
			this.incomingVideoAssignedSrc = src;
		},
		clearIncomingVideo() {
			const video = this.$refs.incomingVideo;
			if (video) {
				try {
					video.pause();
				} catch {
					// Ignore pause errors.
				}
				video.removeAttribute('src');
				video.load();
			}
			this.incomingVideoAssignedSrc = '';
		},
		pauseIncomingVideo() {
			const video = this.$refs.incomingVideo;
			if (video) {
				try {
					video.pause();
				} catch {
					// Ignore pause errors.
				}
				video.muted = true;
			}
		},
		async resolveMomentLength(momentItem) {
			const explicitLength = Number(momentItem?.length);
			if (Number.isFinite(explicitLength) && explicitLength > 0) {
				return explicitLength;
			}

			return 12;
		},
		emitAvailability(isAvailable) {
			this.$emit('availability-changed', isAvailable);
		},
		async wait(ms = 0) {
			await new Promise(resolve => window.setTimeout(resolve, ms));
		},
		async nextAnimationFrame() {
			await new Promise(resolve => window.requestAnimationFrame(() => resolve()));
		},
	},
};
</script>

<style lang="scss">
@import '@shared/sass/shared-variables';

/* stylelint-disable */
.moments-stories-player__modal {
	@include z-index(moments-stories-player);
	position: fixed;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	opacity: 0;
	background: rgba(0, 0, 0, 0);
	pointer-events: none;
	transition: opacity 220ms ease, background-color 220ms ease;
}

.moments-stories-player__frame {
	position: relative;
	aspect-ratio: 9 / 16;
	width: min(
		calc((100vh - 48px) * 9 / 16),
		calc((1100px) * 9 / 16),
		calc(100vw - 48px)
	);
	height: auto;
	max-height: min(calc(100vh - 48px), 1100px);
	border-radius: 26px;
	overflow: hidden;
	background: #000;
	box-shadow: 0 16px 46px rgba(0, 0, 0, 0.38);
	transform-origin: center;
	opacity: 0;
	transform: scale(0.6);
	transition: opacity 220ms ease, transform 220ms ease;
}

.moments-stories-player__modal.-open {
	opacity: 1;
	background: rgba(0, 0, 0, 0.78);
	pointer-events: auto;

	.moments-stories-player__frame {
		opacity: 1;
		transform: scale(1);
	}
}

.moments-stories-player__viewport {
	position: absolute;
	inset: 0;
	overflow: hidden;
}

.moments-stories-player__group-stage {
	position: absolute;
	inset: 0;
	background: #000;
	transition: transform 260ms ease;

	&.-incoming {
		@include z-index(stage-incoming, moments-stories-player);
		transform: translateX(100%);

		&.-to-prev {
			transform: translateX(-100%);
		}

		&.-transitioning {
			transform: translateX(0);
		}

		&.-pinned {
			transform: translateX(0);
			transition: none;
		}

		&.-repositioning {
			transition: none;
		}
	}

	&.-current {
		@include z-index(stage-current, moments-stories-player);
		transform: translateX(0);

		&.-to-next.-transitioning {
			transform: translateX(-100%);
		}

		&.-to-prev.-transitioning {
			transform: translateX(100%);
		}

		&.-pinned {
			transform: translateX(0);
			transition: none;
		}
	}
}

.moments-stories-player__media {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	background: #000;
	opacity: 0;
	transition: none;

	&.-active {
		@include z-index(media-active, moments-stories-player);
		opacity: 1;
	}

	&.-idle {
		@include z-index(media-idle, moments-stories-player);
		opacity: 0;
		pointer-events: none;
	}
}

.moments-stories-player__overlay-top-shadow {
	@include z-index(head-shadow, moments-stories-player);
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 112px;
	background: linear-gradient(180deg, rgba(0, 0, 0, 0.42) 0%, rgba(0, 0, 0, 0) 100%);
	pointer-events: none;
}

.moments-stories-player__nav {
	@include z-index(navigation, moments-stories-player);
	position: absolute;
	top: 0;
	bottom: 0;
	width: 50%;
	border: 0;
	padding: 0;
	background: transparent;
	cursor: pointer;

	&.-prev {
		left: 0;
	}

	&.-next {
		right: 0;
	}
}

.moments-stories-player__loading {
	@include z-index(loading, moments-stories-player);
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	pointer-events: none;

	&.-with-backdrop {
		background: rgba(0, 0, 0, 0.24);
	}
}

.moments-stories-player__loading-spinner {
	@include z-index(loading-spinner, moments-stories-player);
	width: 34px;
	height: 34px;
	border-radius: 50%;
	border: 3px solid rgba(255, 255, 255, 0.35);
	border-top-color: #fff;
	animation: moments-stories-player-spin 0.8s linear infinite;
}

@keyframes moments-stories-player-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (max-width: 1024px) {
	.moments-stories-player__modal,
	.moments-stories-player__modal.-open {
		padding: 0;
		background: var(--color-black);

		body.-t-light & {
			background: var(--color-white);
		}
	}

	.moments-stories-player__modal {
		transition: opacity 220ms ease;
	}

	.moments-stories-player__frame {
		width: 100vw;
		max-width: 100vw;
		height: auto;
		max-height: min(100vh, 1100px);
		border-radius: 0;
		opacity: 1;
		transform: translateY(100%);
		transition: transform 260ms ease;
	}

	.moments-stories-player__modal.-open .moments-stories-player__frame {
		opacity: 1;
		transform: translateY(0);
	}
}
/* stylelint-enable */
</style>
